import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { consultarInmueble } from '../services/api';

const PropertyDetailPage = () => {
  const { consecutivo } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      if (!consecutivo) {
        setError('Código de inmueble no válido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const propertyData = await consultarInmueble(consecutivo);
        setProperty(propertyData);
      } catch (err) {
        console.error('Error fetching property:', err);
        setError('No se pudo cargar la información del inmueble');
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [consecutivo]);

  const formatPrice = (precio) => {
    if (!precio || precio === 0) return null;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(precio);
  };

  const getPriceInfo = (property) => {
    const ventaPrice = property.precio_venta || 0;
    const arriendoPrice = property.precio || property.valor || 0;
    
    const prices = [];
    
    if (ventaPrice > 0) {
      prices.push({
        type: 'Venta',
        price: formatPrice(ventaPrice)
      });
    }
    
    if (arriendoPrice > 0) {
      prices.push({
        type: 'Arriendo',
        price: formatPrice(arriendoPrice)
      });
    }
    
    return prices.length > 0 ? prices : [{type: 'Precio', price: 'No disponible'}];
  };

  const formatArea = (area) => {
    return area ? `${area} m²` : 'No disponible';
  };

  // Generate SEO data from property information
  const generateSEOData = (property) => {
    if (!property) return {};

    // Use API fields first, fallback to generated content
    const title = property.metatitle || 
      `${property.clase} en ${property.municipio}${property.barrio ? `, ${property.barrio}` : ''} - ${formatPrice(property.precio_venta)}`;

    const description = property.metadescripcion || 
      property.resumen || 
      `${property.clase} en ${property.municipio}${property.barrio ? `, barrio ${property.barrio}` : ''}. ${property.area ? `Área: ${property.area} m²` : ''} ${property.alcobas ? `, ${property.alcobas} alcoba${property.alcobas > 1 ? 's' : ''}` : ''} ${property.baños ? `, ${property.baños} baño${property.baños > 1 ? 's' : ''}` : ''}. ${getPriceInfo(property).map(p => `${p.type}: ${p.price}`).join(', ')}`;

    const keywords = property.metakeywords || 
      `${property.clase}, ${property.municipio}, ${property.barrio}, ${property.tipo_servicio}, inmueble, propiedad raíz, bienes raíces`;

    // Generate canonical URL
    const canonicalUrl = `${window.location.origin}/inmueble/${property.consecutivo}`;

    // Get first image for Open Graph
    const ogImage = property.imagenes && property.imagenes.length > 0 
      ? property.imagenes[0].fotourl 
      : null;

    return {
      title,
      description,
      keywords,
      canonicalUrl,
      ogTitle: title,
      ogDescription: description.substring(0, 160), // Limit for social media
      ogImage,
      ogUrl: canonicalUrl,
      twitterCard: 'summary_large_image',
      twitterTitle: title,
      twitterDescription: description.substring(0, 160),
      twitterImage: ogImage
    };
  };

  const getImageUrl = (imagen) => {
    if (!imagen) return null;
    // The images come with full URLs in the fotourl property
    return imagen.fotourl || imagen;
  };

  const nextImage = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (property?.imagenes && property.imagenes.length > 0) {
      setCurrentImageIndex((prev) => 
        (prev + 1) % property.imagenes.length
      );
    }
  };

  const prevImage = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (property?.imagenes && property.imagenes.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? property.imagenes.length - 1 : prev - 1
      );
    }
  };

  const openGallery = (index = null) => {
    if (index !== null) {
      setCurrentImageIndex(index);
    }
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isGalleryOpen) return;
    
    if (e.key === 'Escape') {
      closeGallery();
    } else if (e.key === 'ArrowLeft') {
      prevImage();
    } else if (e.key === 'ArrowRight') {
      nextImage();
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryOpen]);

  const placeholderSvg = (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 600 400"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="600" height="400" fill="#1a1a1a" />
      <g opacity="0.6">
        <path
          d="M200 180h200v40H200z"
          fill="#d4af37"
        />
        <circle cx="250" cy="160" r="20" fill="#d4af37" />
        <circle cx="350" cy="160" r="20" fill="#d4af37" />
        <path
          d="M220 240h160v20H220z"
          fill="#d4af37"
        />
      </g>
      <text
        x="300"
        y="290"
        textAnchor="middle"
        className="fill-yellow-400 text-sm"
        fontSize="14"
      >
        Imagen no disponible
      </text>
    </svg>
  );

  if (loading) {
    return (
      <>
        <SEO title="Cargando inmueble..." description="Cargando información del inmueble" />
        <div className="bg-black min-h-screen">
          <div className="container mx-auto px-4 py-8 pt-28">
            <div className="flex justify-center items-center min-h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
                <p className="text-yellow-400">Cargando información del inmueble...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEO title="Error al cargar inmueble" description="No se pudo cargar la información del inmueble" />
        <div className="bg-black min-h-screen">
          <div className="container mx-auto px-4 py-8 pt-24">
            <div className="bg-red-900 border border-red-700 rounded-lg p-6 text-center">
              <p className="text-red-300 mb-4">{error}</p>
              <button
                onClick={() => navigate('/properties')}
                className="bg-yellow-600 text-black px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors font-semibold"
              >
                Volver a Propiedades
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!property) {
    return (
      <>
        <SEO title="Inmueble no encontrado" description="El inmueble solicitado no fue encontrado" />
        <div className="bg-black min-h-screen">
          <div className="container mx-auto px-4 py-8 pt-24">
            <div className="text-center">
              <p className="text-yellow-400 mb-4">Inmueble no encontrado</p>
              <button
                onClick={() => navigate('/properties')}
                className="bg-yellow-600 text-black px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors font-semibold"
              >
                Volver a Propiedades
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const hasImages = property.imagenes && property.imagenes.length > 0;
  const seoData = generateSEOData(property);

  return (
    <>
      <SEO {...seoData} />
      <div className="bg-black min-h-screen">
        <div className="container mx-auto px-4 py-8 pt-24">
          {/* Back button */}
          <button
            onClick={() => navigate('/properties')}
            className="mb-6 flex items-center text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver a Propiedades
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Carousel */}
            <div className="relative">
              <div 
                className="h-[33rem] bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-lg overflow-hidden border border-yellow-600 cursor-pointer group"
                onClick={() => hasImages && openGallery()}
              >
                {hasImages ? (
                  <>
                    <div className="relative w-full h-full flex items-center justify-center">
                      <img
                        src={getImageUrl(property.imagenes[currentImageIndex])}
                        alt={property.imagenes[currentImageIndex].alt || `${property.clase} en ${property.municipio}`}
                        className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-full items-center justify-center">
                        {placeholderSvg}
                      </div>
                    </div>
                    {/* Overlay for click indication */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black bg-opacity-70 rounded-full p-3">
                        <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {placeholderSvg}
                  </div>
                )}

                {/* Navigation arrows */}
                {hasImages && property.imagenes.length > 1 && (
                  <>
                    <button
                      onClick={(e) => prevImage(e)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-70 text-yellow-400 p-2 rounded-full hover:bg-opacity-90 transition-opacity border border-yellow-600 z-10"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => nextImage(e)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-70 text-yellow-400 p-2 rounded-full hover:bg-opacity-90 transition-opacity border border-yellow-600 z-10"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}

                {/* Image indicator */}
                {hasImages && property.imagenes.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {property.imagenes.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(index);
                        }}
                        className={`w-3 h-3 rounded-full border z-10 ${
                          index === currentImageIndex ? 'bg-yellow-400 border-yellow-400' : 'bg-transparent border-yellow-400'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Property Information */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  {property.clase} para el {property.tipo_servicio.toLowerCase()==='ambos'?'Arriendo y Venta':property.tipo_servicio} en {property.municipio}
                </h1>
                {property.barrio && (
                  <p className="text-lg text-yellow-400 mb-4">
                    Barrio: {property.barrio}
                  </p>
                )}
                <div className="space-y-2">
                  {getPriceInfo(property).map((priceInfo, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-lg text-gray-300">{priceInfo.type}:</span>
                      <span className="text-2xl font-bold text-yellow-400">{priceInfo.price}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key features */}
              <div className="grid grid-cols-2 gap-4">
                {property.areaconstruida && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="text-sm text-yellow-400">Área Construida</div>
                    <div className="text-lg font-semibold text-white">{formatArea(property.areaconstruida)}</div>
                  </div>
                )}
                {property.area && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="text-sm text-yellow-400">Área Total</div>
                    <div className="text-lg font-semibold text-white">{formatArea(property.area)}</div>
                  </div>
                )}
                {property.alcobas && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="text-sm text-yellow-400">Habitaciones</div>
                    <div className="text-lg font-semibold text-white">{property.alcobas}</div>
                  </div>
                )}
                {property.baños && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="text-sm text-yellow-400">Baños</div>
                    <div className="text-lg font-semibold text-white">{property.baños}</div>
                  </div>
                )}
                {property.garaje > 0 && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="text-sm text-yellow-400">Parqueaderos</div>
                    <div className="text-lg font-semibold text-white">{property.garaje}</div>
                  </div>
                )}
                {property.estrato && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="text-sm text-yellow-400">Estrato</div>
                    <div className="text-lg font-semibold text-white">{property.estrato}</div>
                  </div>
                )}
                {property.vlr_admon > 0 && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="text-sm text-yellow-400">Administración</div>
                    <div className="text-lg font-semibold text-white">{formatPrice(property.vlr_admon)}</div>
                  </div>
                )}
                {property.nivel && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="text-sm text-yellow-400">Piso</div>
                    <div className="text-lg font-semibold text-white">{property.nivel}</div>
                  </div>
                )}
              </div>

                 {/* /* Contact button */}
                <a
                  href={`https://wa.me/573127244825?text=Hola,%20quiero%20más%20información%20sobre%20el%20inmueble%20 - ${property.consecutivo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-yellow-600 text-black py-3 px-6 rounded-lg hover:bg-yellow-500 transition-colors text-lg font-bold shadow-lg flex items-center justify-center"
                >
                  Contactar Asesor
                </a>
            </div>
        </div>
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-6">Características de la Propiedad</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* {property.tipo_servicio && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Tipo de Servicio: {property.tipo_servicio}</span>
                  </div>
                </div>
              )} */}
              {property.unidad_abierta && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Unidad Abierta: {property.unidad_abierta}</span>
                  </div>
                </div>
              )}
              {property.unidad_cerrada && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Unidad Cerrada: {property.unidad_cerrada}</span>
                  </div>
                </div>
              )}
              {property.urbanizacion && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Urbanización: {property.urbanizacion}</span>
                  </div>
                </div>
              )}
              {property.closets > 0 && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Closets: {property.closets}</span>
                  </div>
                </div>
              )}
              {property.cocina > 0 && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Cocina: {property.cocina === 1 ? 'Sencilla' : property.cocina === 2 ? 'Integral' : 'Personalizada'}</span>
                  </div>
                </div>
              )}
              {property.zona_recidencial && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Zona Residencial</span>
                  </div>
                </div>
              )}
              {property.zona_comercial && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Zona Comercial</span>
                  </div>
                </div>
              )}
              {property.sobre_via_ppal && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white">Sobre Vía Principal</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Home Amenities - Dynamic display */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-6">Comodidades del Hogar</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {property.sala_comedor && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm3 5a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Sala Comedor</div>
                </div>
              )}
              {property.sala && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6z" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Sala</div>
                </div>
              )}
              {property.comedor && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm3 8a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1z" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Comedor</div>
                </div>
              )}
              {property.chimenea && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Chimenea</div>
                </div>
              )}
              {property.terraza && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.559-.499-.948-.737-1.182C10.232 4.032 10.076 4 10 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Terraza</div>
                </div>
              )}
              {property.jacuzzi && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.559-.499-.948-.737-1.182C10.232 4.032 10.076 4 10 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Jacuzzi</div>
                </div>
              )}
              {property.zona_verde && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Zona Verde</div>
                </div>
              )}
              {property.antejardin && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 6.707 6.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Antejardín</div>
                </div>
              )}
              {property.vestier > 0 && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Vestier</div>
                </div>
              )}
              {property.cuartoutil && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 100 2h.01a1 1 0 100-2H10zm-4 1a1 1 0 011-1h.01a1 1 0 110 2H7a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Cuarto Útil</div>
                </div>
              )}
              {property.zonaropas && (
                <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg text-center">
                  <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-white text-sm font-medium">Zona de Ropas</div>
                </div>
              )}
            </div>
          </div>

          {/* Amenities */}
          {(property.piscina || property.gimnasio || property.juegos || property.turco || property.porteria) && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-white mb-6">Amenidades</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {property.piscina && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">Piscina</span>
                    </div>
                  </div>
                )}
                {property.gimnasio && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">Gimnasio</span>
                    </div>
                  </div>
                )}
                {property.juegos && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">Zona de Juegos</span>
                    </div>
                  </div>
                )}
                {property.turco && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">Turco</span>
                    </div>
                  </div>
                )}
                {property.porteria && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">Portería 24 horas</span>
                    </div>
                  </div>
                )}
                {property.ascensor && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">Ascensor</span>
                    </div>
                  </div>
                )}
                {property.balcon && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">Balcón</span>
                    </div>
                  </div>
                )}
                {property.parqueaderovisitantes && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">Parqueadero Visitantes</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Nearby Services */}
          {(property.colegios_cercanos || property.universidades_cercanas || property.parques_cercanos || property.supermercados_cercanos || property.centros_comerciales_cercanos || property.transporte_publico_cercano) && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-white mb-6">Servicios Cercanos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {property.colegios_cercanos && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                        </svg>
                      </div>
                      <span className="text-white">Colegios Cercanos</span>
                    </div>
                  </div>
                )}
                {property.universidades_cercanas && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                        </svg>
                      </div>
                      <span className="text-white">Universidades Cercanas</span>
                    </div>
                  </div>
                )}
                {property.parques_cercanos && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-white">Parques Cercanos</span>
                    </div>
                  </div>
                )}
                {property.supermercados_cercanos && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-white">Supermercados Cercanos</span>
                    </div>
                  </div>
                )}
                {property.centros_comerciales_cercanos && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm3 5a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm0 3a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-white">Centros Comerciales</span>
                    </div>
                  </div>
                )}
                {property.transporte_publico_cercano && (
                  <div className="bg-gray-900 border border-yellow-600 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-white">Transporte Público</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional characteristics */}
          {property.resumen && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-white mb-6">Resumen</h2>
              <div className="bg-gray-900 border border-yellow-600 p-6 rounded-lg">
                <div 
                  className="text-gray-300 leading-relaxed [&>p]:mb-4 [&>ul]:mb-4 [&>li]:mb-2 [&>h1]:text-xl [&>h1]:font-bold [&>h1]:text-white [&>h1]:mb-3 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:text-yellow-400 [&>h2]:mb-2 [&>h3]:text-base [&>h3]:font-medium [&>h3]:text-yellow-400 [&>h3]:mb-2 [&>strong]:text-white [&>strong]:font-semibold [&>em]:text-yellow-400 [&>a]:text-yellow-400 [&>a]:underline"
                  dangerouslySetInnerHTML={{ __html: property.resumen }}
                />
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Full-Screen Gallery Modal */}
      {isGalleryOpen && hasImages && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeGallery();
            }
          }}
        >
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeGallery();
            }}
            className="absolute top-4 right-4 z-60 bg-black bg-opacity-70 text-white p-3 rounded-full hover:bg-opacity-90 transition-all border border-yellow-600 z-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image counter */}
          <div className="absolute top-4 left-4 z-60 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg border border-yellow-600">
            <span className="text-yellow-400 font-semibold">
              {currentImageIndex + 1} / {property.imagenes.length}
            </span>
          </div>

          {/* Navigation arrows */}
          {property.imagenes.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage(e);
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-60 bg-black bg-opacity-70 text-yellow-400 p-4 rounded-full hover:bg-opacity-90 transition-all border border-yellow-600 z-50"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage(e);
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-60 bg-black bg-opacity-70 text-yellow-400 p-4 rounded-full hover:bg-opacity-90 transition-all border border-yellow-600 z-50"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Main image */}
          <div className="relative w-full h-full flex items-center justify-center p-8">
            <img
              src={getImageUrl(property.imagenes[currentImageIndex])}
              alt={property.imagenes[currentImageIndex].alt || `${property.clase} en ${property.municipio}`}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden w-full h-full items-center justify-center">
              {placeholderSvg}
            </div>
          </div>

          {/* Thumbnail strip */}
          {property.imagenes.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 max-w-full overflow-x-auto p-2 bg-black bg-opacity-50 rounded-lg">
              {property.imagenes.map((imagen, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentImageIndex 
                      ? 'border-yellow-400 opacity-100' 
                      : 'border-gray-600 opacity-60 hover:opacity-80'
                  }`}
                >
                  <img
                    src={getImageUrl(imagen)}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PropertyDetailPage;
